import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Card, Form, Badge, OverlayTrigger, Tooltip, Alert} from 'react-bootstrap';
import {Globe, RefreshCw, Trash2, Plus, Search, Edit2} from 'lucide-react';
import {PageTransition, useModal} from '../../../components/layout';
import { useAuth } from '../../../contexts/AuthContext';
import {API_ENDPOINTS} from '../../../config/api.config.ts';
import WebSourceForm from '../../../components/data-sources/web-sources/WebSourceForm.tsx';
import {WebSource, WebSourceFormData} from '../../../types';
import {transformFormDataToWebSource} from "../../../utils/webSourceUtils.ts";
import { apiService } from '../../../services/apiService';
import DeleteConfirmationModal from '../../../components/modals/common/DeleteConfirmationModal';
import '../../../../scss/custom.scss';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

interface WebSourceResponse {
    webSources: WebSource[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
}

interface UsageResponse {
    knowledgeBases: Array<{ id: string; name: string }>;
}

interface PaginationData {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

const ITEMS_PER_PAGE = 10;

const Index: React.FC = () => {
    useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [sourceToDelete, setSourceToDelete] = useState<WebSource | null>(null);
    const navigate = useNavigate();
    const {user} = useAuth();
    const modalState = useModal();
    const [sources, setSources] = useState<WebSource[]>([]);
    const [selectedSource, setSelectedSource] = useState<WebSource | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingSource, setEditingSource] = useState<WebSource | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        limit: ITEMS_PER_PAGE,
        total: 0,
        totalPages: 0
    });

    const fetchSources = async (page: number, search?: string) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ITEMS_PER_PAGE.toString(),
                ...(search ? { search } : {})
            });

            const response = await apiService.get<WebSourceResponse>(
                `${API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.LIST}?${queryParams}`
            );

            // Fetch usage data for each source
            const sourcesWithUsage = await Promise.all(
                (response.webSources || []).map(async (source: WebSource) => {
                    try {
                        const usageData = await apiService.get<UsageResponse>(
                            API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.USAGE.replace(':id', source.id)
                        );
                        return {
                            ...source,
                            used_by_kbs: usageData.knowledgeBases
                        };
                    } catch (error) {
                        console.error(`Failed to fetch usage for source ${source.id}:`, error);
                        return source;
                    }
                })
            );

            setSources(sourcesWithUsage);
            setCurrentPage(response.page);
            setPagination({
                page: response.page,
                limit: response.limit,
                total: response.total,
                totalPages: response.totalPages
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch websites');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSources(1);
    }, []);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        fetchSources(newPage, searchTerm);
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
        fetchSources(1, value);
    };

    const handleAddSource = async (formData: WebSourceFormData) => {
        try {
            const transformedData = transformFormDataToWebSource(formData);
            await apiService.post(
                API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.CREATE,
                transformedData
            );

            await fetchSources(currentPage, searchTerm);
            setShowAddForm(false);
            modalState.setSuccess('Web source added successfully');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add website');
        }
    };

    const handleUpdateSource = async (formData: WebSourceFormData) => {
        if (!editingSource) return;
        try {
            const transformedData = transformFormDataToWebSource(formData);
            await apiService.put(
                API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.UPDATE.replace(':id', editingSource.id),
                transformedData
            );

            await fetchSources(currentPage, searchTerm);
            setEditingSource(null);
            modalState.setSuccess('Web source updated successfully');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update web source');
        }
    };

    const handleDeleteSource = (source: WebSource) => {
        setSourceToDelete(source);
        setShowDeleteModal(true);
    };

    const handleSyncSource = async (sourceId: string) => {
        try {
            await apiService.post(
                API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.SYNC.replace(':id', sourceId),
                {}
            );
            modalState.setSuccess('Web source sync initiated');
            await fetchSources(currentPage, searchTerm);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sync web source');
        }
    };

    const confirmDelete = async () => {
        if (!sourceToDelete) return;

        try {
            await apiService.delete(
                API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.DELETE.replace(':id', sourceToDelete.id)
            );
            await fetchSources(currentPage, searchTerm);
            setSelectedSource(null);
            modalState.setSuccess('Web source deleted successfully');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete web source');
        } finally {
            setShowDeleteModal(false);
            setSourceToDelete(null);
        }
    };

    const renderPagination = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Web sources pagination">
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages}
                    {endPage < pagination.totalPages && (
                        <>
                            {endPage < pagination.totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                >
                                    {pagination.totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (loading) {
        return (
            <>
                <h5 className="mb-3">Websites</h5>
                <ContentSkeleton />
            </>
        );
    }

    if (!user) {
        return (
            <PageTransition>
                <div className="container mt-5">
                    <Alert variant="warning">Please log in to view web sources.</Alert>
                </div>
            </PageTransition>
        );
    }

    return (
        <div className="page-container">
            <div className="mb-4">
                <h5 className="mb-3">Websites</h5>
                {loading ? (
                    <ContentSkeleton />
                ) : (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-4">
                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        {modalState.success && (
                            <Alert variant="success" onClose={() => modalState.setSuccess(null)} dismissible>
                                {modalState.success}
                            </Alert>
                        )}

                        {(showAddForm || editingSource) && (
                            <div className="mb-4">
                                <WebSourceForm
                                    onSubmit={editingSource ? handleUpdateSource : handleAddSource}
                                    onCancel={() => {
                                        setShowAddForm(false);
                                        setEditingSource(null);
                                    }}
                                    initialData={editingSource ? {
                                        name: editingSource.name,
                                        description: editingSource.description || '',
                                        url: editingSource.configuration.url,
                                        crawl_frequency: editingSource.configuration.crawl_frequency as 'daily' | 'weekly' | 'monthly',
                                        sitemap_url: editingSource.configuration.sitemap_url || '',
                                        crawl_depth: editingSource.configuration.crawl_depth || 2
                                    } : undefined}
                                    isEdit={!!editingSource}
                                />
                            </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div className="flex-grow-1">
                                <div className="mt-4">
                                    <Form.Group>
                                        <div className="d-flex gap-2">
                                            <div className="position-relative flex-grow-1">
                                                <div className="position-absolute top-50 translate-middle-y ps-3">
                                                    <Search size={16} className="text-muted"/>
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search websites..."
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    className="ps-5 border"
                                                    style={{height: '38px'}}
                                                />
                                            </div>
                                            <button
                                                className="btn btn-outline-primary d-flex align-items-center gap-2"
                                                onClick={() => navigate('/data-sources/web/create')}
                                                style={{height: '38px', whiteSpace: 'nowrap'}}
                                            >
                                                <Plus size={16}/>
                                                Add Website
                                            </button>
                                        </div>
                                    </Form.Group>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-8">
                                {sources.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        No web sources found.
                                    </div>
                                ) : (
                                    <>
                                        <div className="list-group">
                                            {sources.map((source) => (
                                                <div
                                                    key={source.id}
                                                    className={`list-group-item py-2 px-3 ${
                                                        selectedSource?.id === source.id ? 'selected' : ''
                                                    }`}
                                                    onClick={() => setSelectedSource(source.id === selectedSource?.id ? null : source)}
                                                >
                                                    <div className="d-flex align-items-start">
                                                        <Globe size={18} className="text-muted me-2 mt-1"/>
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <h6 className="mb-0 text-truncate">{source.name}</h6>
                                                                <OverlayTrigger
                                                                    placement="left"
                                                                    delay={{ show: 100, hide: 100 }}
                                                                    overlay={(props) =>
                                                                        source.used_by_kbs && source.used_by_kbs.length > 0 ? (
                                                                            <Tooltip id={`status-tooltip-${source.id}`} {...props}>
                                                                                <div className="text-start">
                                                                                    <div className="mb-1">Used in Knowledge Bases:</div>
                                                                                    <ul className="list-unstyled m-0">
                                                                                        {source.used_by_kbs.map(kb => (
                                                                                            <li key={kb.id}>{kb.name}</li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            </Tooltip>
                                                                        ) : <></>
                                                                    }
                                                                >
                                                                    <Badge
                                                                        className={`ms-2 px-2 py-1 text-xs ${
                                                                            source.used_by_kbs && source.used_by_kbs.length > 0
                                                                                ? 'bg-success-subtle text-success-emphasis'
                                                                                : 'bg-secondary-subtle text-secondary-emphasis'
                                                                        }`}
                                                                    >
                                                                        {source.used_by_kbs && source.used_by_kbs.length > 0 ? 'In Use' : 'Not Used'}
                                                                    </Badge>
                                                                </OverlayTrigger>
                                                            </div>
                                                            <div className="d-flex align-items-end">
                                                                <div className="flex-grow-1">
                                                                    {source.description && (
                                                                        <p className="text-muted small mb-1"
                                                                           style={{whiteSpace: 'pre-line'}}>
                                                                            {source.description}
                                                                        </p>
                                                                    )}
                                                                    <div className="text-muted small">
                                                                        <a
                                                                            href={source.configuration.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            className="text-decoration-none"
                                                                        >
                                                                            {source.configuration.url}
                                                                        </a>
                                                                        {source.configuration.crawl_frequency && (
                                                                            <span className="ms-2">
                                                                            · Crawls {source.configuration.crawl_frequency}
                                                                                {source.configuration.crawl_depth && (
                                                                                    <span> · Depth: {source.configuration.crawl_depth}</span>
                                                                                )}
                                                                        </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="text-muted small ms-3"
                                                                    style={{
                                                                        minWidth: '100px',
                                                                        textAlign: 'right'
                                                                    }}
                                                                >
                                                                    {source.created_at === source.updated_at ? (
                                                                        <>Created on {source.created_at ? new Date(source.created_at).toLocaleDateString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric'
                                                                        }) : 'Unknown date'}</>
                                                                    ) : (
                                                                        <>Updated on {source.updated_at ? new Date(source.updated_at).toLocaleDateString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric'
                                                                        }) : 'Unknown date'}</>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mt-5">
                                            <div className="text-muted small">
                                                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                            </div>
                                            {!loading && pagination.totalPages > 0 && renderPagination()}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="col-md-4">
                                <Card className="border shadow-sm">
                                    <Card.Header className="border-bottom px-3 py-2">
                                        <h6 className="mb-0">Actions</h6>
                                    </Card.Header>
                                    <Card.Body className="p-2">
                                        <div className="d-grid gap-1">
                                            <button
                                                className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                disabled={!selectedSource}
                                                onClick={() => selectedSource && handleSyncSource(selectedSource.id)}
                                            >
                                                <RefreshCw size={16}/>
                                                Sync
                                            </button>
                                            <button
                                                className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                disabled={!selectedSource}
                                                onClick={() => selectedSource && navigate(`/data-sources/web/${selectedSource.id}/edit`)}
                                            >
                                                <Edit2 size={16}/>
                                                Edit
                                            </button>
                                            <button
                                                className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                disabled={!selectedSource}
                                                onClick={() => selectedSource && handleDeleteSource(selectedSource)}
                                            >
                                                <Trash2 size={16}/>
                                                Delete
                                            </button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                )}
            </div>
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSourceToDelete(null);
                }}
                onConfirm={confirmDelete}
                resourceType="Web Source"
                resourceName={sourceToDelete?.name || ''}
                isLoading={loading}
            />
        </div>
    );
};

export default Index;